import type { ReactNode } from 'react';

/**
 * The shared line-icon set for the console.
 *
 * One small, reusable family — no external icon library, no emoji. Every glyph
 * is a hand-drawn inline SVG at a 1.5px stroke in **`currentColor`**, so it
 * takes the ink of whatever text it sits beside and themes automatically in
 * light and dark with nothing to redefine (styles.css never names an icon
 * colour). They are drawn on a 24×24 grid and rendered at ~16–18px.
 *
 * **Icons never stand alone here.** The design bible bans icon-only controls
 * (docs/design-dashboard.md §7.3, WCAG 2.2 AA) — every glyph in this app is
 * paired with the text label that carries the meaning, so each icon is
 * decorative and marked `aria-hidden` / `focusable="false"`. That is why they
 * contribute nothing to a control's accessible name: the label does.
 */

function Svg({ size = 16, children }: { size?: number; children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

type IconProps = { size?: number };

/* ------------------------------------------------------------------ rail nav */

/** Home — the map/command view. A simple house. */
export function HomeIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <path d="M4 10.5 12 4l8 6.5" />
      <path d="M6 9.5V20h12V9.5" />
      <path d="M10 20v-5h4v5" />
    </Svg>
  );
}

/** People — a person and a group behind them. */
export function UsersIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.5a3 3 0 0 1 0 5.6" />
      <path d="M17.5 13.5A5.2 5.2 0 0 1 20.5 19" />
    </Svg>
  );
}

/** Teams — a named grouping. Overlapping members inside a bounded set. */
export function TeamIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <circle cx="8.5" cy="9" r="2.6" />
      <circle cx="15.5" cy="9" r="2.6" />
      <path d="M4 18a4.5 4.5 0 0 1 9 0" />
      <path d="M13 18a4.5 4.5 0 0 1 7-3.4" />
    </Svg>
  );
}

/** Locations — a building (never a lone map pin: these are the org's buildings). */
export function BuildingIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <path d="M5 20V6a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v14" />
      <path d="M15 20V10h3a1 1 0 0 1 1 1v9" />
      <path d="M3 20h18" />
      <path d="M8 8.5h1.5M11.5 8.5H13M8 12h1.5M11.5 12H13M8 15.5h1.5M11.5 15.5H13" />
    </Svg>
  );
}

/** Hardware — a chip/device. The physical ecosystem. */
export function ChipIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <rect x="6.5" y="6.5" width="11" height="11" rx="2" />
      <rect x="10" y="10" width="4" height="4" rx="0.5" />
      <path d="M9 6.5V4M12 6.5V4M15 6.5V4M9 20v-2.5M12 20v-2.5M15 20v-2.5M6.5 9H4M6.5 12H4M6.5 15H4M20 9h-2.5M20 12h-2.5M20 15h-2.5" />
    </Svg>
  );
}

/** Activity — a pulse/trace of what has happened. */
export function ActivityIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <path d="M3 12h4l2.5-6 5 13L17 12h4" />
    </Svg>
  );
}

/** Settings — a gear. */
export function SettingsIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v2.2M12 19.3v2.2M4.2 4.2l1.6 1.6M18.2 18.2l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.2 19.8l1.6-1.6M18.2 5.8l1.6-1.6" />
    </Svg>
  );
}

/** Evidence — a shield with a check: the chain-of-custody workspace. */
export function ShieldIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <path d="M12 3 5 6v5c0 4.4 3 7.6 7 9 4-1.4 7-4.6 7-9V6l-7-3Z" />
      <path d="m9 11.5 2 2 4-4" />
    </Svg>
  );
}

/* -------------------------------------------------- type-column & list glyphs */

/** A single person. */
export function PersonIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5.5 19.5a6.5 6.5 0 0 1 13 0" />
    </Svg>
  );
}

/** Staff — an ID badge. */
export function BadgeIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <rect x="4.5" y="6" width="15" height="13" rx="2" />
      <path d="M9 6V4.5h6V6" />
      <circle cx="12" cy="11" r="1.8" />
      <path d="M9 16a3 3 0 0 1 6 0" />
    </Svg>
  );
}

/** Administrators — a key (holds authority). */
export function KeyIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <circle cx="8" cy="8" r="3.5" />
      <path d="m10.5 10.5 7 7" />
      <path d="m15 15 1.5-1.5M17 17l1.8-1.8" />
    </Svg>
  );
}

/** Restricted — a padlock. */
export function LockIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <rect x="5.5" y="10.5" width="13" height="9" rx="2" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
      <path d="M12 14v2.5" />
    </Svg>
  );
}

/** All — a grid of everything. */
export function GridIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <rect x="4.5" y="4.5" width="6" height="6" rx="1" />
      <rect x="13.5" y="4.5" width="6" height="6" rx="1" />
      <rect x="4.5" y="13.5" width="6" height="6" rx="1" />
      <rect x="13.5" y="13.5" width="6" height="6" rx="1" />
    </Svg>
  );
}

/** A mobile device — panic buttons, wearables, beacons. */
export function DeviceIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <rect x="7" y="3.5" width="10" height="17" rx="2.5" />
      <path d="M10.5 17.5h3" />
    </Svg>
  );
}

/** A vehicle — the fleet. */
export function VehicleIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <path d="M3.5 15V8.5h9V15" />
      <path d="M12.5 10.5H16l2.5 3V15h-6" />
      <path d="M3.5 15H2.5M18.5 15h1" />
      <circle cx="7" cy="16.5" r="1.8" />
      <circle cx="15.5" cy="16.5" r="1.8" />
      <path d="M8.8 16.5h4.9" />
    </Svg>
  );
}

/** A plug — integrations that connect systems. */
export function PlugIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <path d="M9 3.5v4M15 3.5v4" />
      <path d="M6.5 7.5h11v3a5.5 5.5 0 0 1-11 0Z" />
      <path d="M12 16v4.5" />
    </Svg>
  );
}

/** A document — a report. */
export function DocumentIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <path d="M6.5 3.5h7l4 4V20a1 1 0 0 1-1 1H6.5a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" />
      <path d="M13 3.5V8h4" />
      <path d="M8.5 12.5h7M8.5 15.5h7M8.5 9.5h2" />
    </Svg>
  );
}

/** A bell — an event/alert. */
export function BellIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <path d="M6.5 16V11a5.5 5.5 0 0 1 11 0v5l1.5 2H5l1.5-2Z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </Svg>
  );
}

/** A clock — the change log / history. */
export function ClockIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7.5V12l3 2" />
    </Svg>
  );
}

/* ------------------------------------------------------- procedure (event) glyphs */

/** Evacuation — a figure leaving through a door. */
export function ExitIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <path d="M13 4.5H6a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h7" />
      <path d="M10 12h9" />
      <path d="m16 9 3 3-3 3" />
    </Svg>
  );
}

/** Communication — a megaphone announcement. */
export function MegaphoneIcon({ size }: IconProps) {
  return (
    <Svg size={size}>
      <path d="M4 10v4l3 .5V9.5L4 10Z" />
      <path d="m7 9.5 10-4.5v14l-10-4.5" />
      <path d="M8 15v3a1.5 1.5 0 0 0 3 0v-2.3" />
    </Svg>
  );
}
